import {
  DateValue,
  fromDate,
  getLocalTimeZone,
  toCalendarDate,
  today,
} from '@internationalized/date';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DATE_FNS_DATE_ONLY_ISO_FORMAT,
  DATE_FNS_UK_DATE_FORMAT,
  DATE_FNS_UK_DATE_SLASH_FORMAT,
  DateOnlyISOString,
  formatDate,
  getYear,
  isValidDateString,
  parseDate,
} from '../../../utils/dateUtil';
import { errorToast } from '../../../utils/toast';
import { useCommonTranslation } from '../../../hooks/i18n/useCommonTranslation';

export const DATE_PICKER_VALUE_FORMAT = DATE_FNS_DATE_ONLY_ISO_FORMAT;
export const DATE_PICKER_USER_FORMAT = DATE_FNS_UK_DATE_FORMAT;

const PASTE_ACCEPTED_FORMATS = [
  DATE_FNS_UK_DATE_FORMAT,
  DATE_FNS_UK_DATE_SLASH_FORMAT,
  DATE_FNS_DATE_ONLY_ISO_FORMAT,
];

export type DatePickerInputValue = DateOnlyISOString | undefined;

export type DatePickerInputOnChange = (
  val: DateOnlyISOString | undefined,
) => void;

const toDateValue = (date: Date): DateValue => {
  return toCalendarDate(fromDate(date, getLocalTimeZone()));
};

const toDate = (dateValue: DateValue): Date => {
  return dateValue.toDate(getLocalTimeZone());
};

/**
 * What is the motivation behind `pendingOnChangeEvents`:
 *
 * `ReactAriaDatePicker` component can work as controlled or uncontrolled input
 * in similar way as standard React input component https://react.dev/reference/react-dom/components/input.
 *
 * `DatePickerInput` supports controlled variant only.
 * `ReactAriaDatePicker` allows user to partially delete the date value, like deleting year or month.
 * It results in an empty date value that ReactAriaDatePicker triggers through onChange
 * callback. It is valid behavior, but works well in uncontrolled variant only.
 *
 * In controlled variant, the partial value is overridden by empty input value
 * passed from controlling component, because it received the empty value through onChange
 * callback and converted it to the empty value immediately.
 *
 * The only way how to support partial date value in `DatePickerInput` is
 * to present `ReactAriaDatePicker` in the uncontrolled variant and re-render
 * it completely when the controlled value is changed from parent component directly (it is usually rare).
 * Re-rendering is done by updating the `key` prop of `ReactAriaDatePicker` with an incrementing counter.
 *
 * To distinguish the value change triggered by `ReactAriaDatePicker` and parent component,
 * we keep track of the pending onChange event triggered by `ReactAriaDatePicker` in `pendingOnChangeEvents` set.
 * The logic is the following:
 *
 *  1. `ReactAriaDatePicker` triggers onChange event.
 *  2. We add the `DatePickerInput` key with the new value into `pendingOnChangeEvents`.
 *  3. Parent component receives the new value from onChange event and triggers value change.
 *  4. `DatePickerInput` receives the new value and checks if the key is in `pendingOnChangeEvents` and if the value matches.
 *  4a. If the key is present and the value matches, it means that the value change was triggered by `ReactAriaDatePicker`
 *  and we don't re-render it.
 *  4b. If the key is not present or the value doesn't match, it means that the value change was triggered by parent component
 *  and we re-render `ReactAriaDatePicker` to update the `defaultValue`.
 */
const pendingOnChangeEvents = new Map<string, DatePickerInputValue>();

export const useDatePickerInputData = ({
  usingCustomInput,
  onChange,
  value,
  filterDate,
  maxDate,
  minDate,
}: {
  usingCustomInput: boolean;
  onChange: DatePickerInputOnChange;
  value?: DatePickerInputValue;
  filterDate?: (date: Date) => boolean | undefined;
  maxDate?: Date;
  minDate?: Date;
}) => {
  const { t } = useCommonTranslation();
  const [dateValue, setDateValue] = useState<null | DateValue>(null);
  const datePickerKey = useMemo(() => uuidv4(), []);
  const [renderCounter, setRenderCounter] = useState(0);

  useEffect(() => {
    return () => {
      if (!usingCustomInput) {
        pendingOnChangeEvents.delete(datePickerKey);
      }
    };
  }, [usingCustomInput, datePickerKey]);

  useEffect(() => {
    if (isValidDateString(value)) {
      setDateValue(toDateValue(parseDate(value, DATE_PICKER_VALUE_FORMAT)));
    } else {
      setDateValue(null);
    }
    if (!usingCustomInput) {
      if (
        pendingOnChangeEvents.has(datePickerKey) &&
        pendingOnChangeEvents.get(datePickerKey) === value
      ) {
        pendingOnChangeEvents.delete(datePickerKey);
      } else {
        setRenderCounter((prev) => prev + 1);
      }
    }
  }, [value, datePickerKey, usingCustomInput]);

  const handleOnChange = useCallback(
    (date: DateValue | string | null, externalChange = false) => {
      let dateStr: string | undefined = undefined;
      let addEventToPending = externalChange ? false : true;
      if (typeof date === 'string') {
        dateStr = date;
      } else if (date) {
        if (date.era === 'BC') {
          // We don't support BC date, lets move the date back to current year.
          date = date.set({
            era: 'AD',
            year: getYear(new Date()),
          });
          addEventToPending = false;
        }
        dateStr = formatDate(toDate(date), DATE_PICKER_VALUE_FORMAT);
      } else {
        dateStr = undefined;
      }
      if (!usingCustomInput && addEventToPending) {
        pendingOnChangeEvents.set(datePickerKey, dateStr);
      }
      onChange(dateStr);
    },
    [onChange, datePickerKey, usingCustomInput],
  );

  const handleCopy = useCallback(
    (event: React.ClipboardEvent) => {
      event.preventDefault();
      if (!dateValue) {
        return;
      }
      const formatted = formatDate(toDate(dateValue), DATE_PICKER_USER_FORMAT);
      event.clipboardData.setData('text/plain', formatted);
    },
    [dateValue],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      event.preventDefault();
      let text = event.clipboardData.getData('text/plain').trim();
      if (!text) {
        return;
      }
      text = text.replace(/\s/g, '');
      for (const format of PASTE_ACCEPTED_FORMATS) {
        if (isValidDateString(text, format)) {
          const date = toDateValue(parseDate(text, format));
          handleOnChange(date, true);
          return;
        }
      }
      errorToast(
        t('date-picker-paste-accepted-formats', {
          acceptedFormats: PASTE_ACCEPTED_FORMATS.join(', '),
        }),
      );
    },
    [handleOnChange, t],
  );

  return {
    handleOnChange,
    handleCopy,
    handlePaste,
    inputValue: value,
    defaultValue: dateValue,
    renderKey: `${datePickerKey}-${renderCounter}`,
    isDateUnavailable: filterDate
      ? (date: DateValue) => !filterDate(toDate(date))
      : undefined,
    maxValue: maxDate ? toDateValue(maxDate) : undefined,
    minValue: minDate ? toDateValue(minDate) : undefined,
    placeholderValue: toCalendarDate(today(getLocalTimeZone())),
  };
};
